import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import { type ForwardedRef, forwardRef, useState } from 'react';

import { Flex } from '../../flex';
import type { SelectionProps } from '../Selection';
import type { OptionSelectionProps } from '../types';

type ChipSelectionProps = Pick<SelectionProps, 'value' | 'defaultValue' | 'sx'> & {
  label?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  options: OptionSelectionProps[];
  row?: boolean;
  onChange?: (value: OptionSelectionProps['value']) => void;
  onBlur?: () => void;
};

const ChipSelection = forwardRef((props: ChipSelectionProps, ref: ForwardedRef<HTMLDivElement>) => {
  const {
    value,
    defaultValue,
    options,
    onChange,
    label,
    helperText,
    row: hasRow,
    disabled: isDisabled,
    onBlur,
    ...restProps
  } = props;

  const [selectedValue, setSelectedValue] = useState(value ?? defaultValue);

  const handleChange = (value: OptionSelectionProps['value']) => {
    setSelectedValue(value);
    onChange?.(value);
    onBlur?.();
  };

  return (
    <FormControl {...restProps} ref={ref} fullWidth={!hasRow}>
      {label ? <InputLabel sx={{ position: 'relative', mb: 2 }}>{label}</InputLabel> : null}
      <Flex
        // TODO: Chips align center by parent element
        sx={{
          justifyContent: 'center',
          flexDirection: hasRow ? 'row' : 'column',
          flexWrap: hasRow ? 'wrap' : 'nowrap',
          gap: 2,
        }}
      >
        {options.map((option, index) => (
          <Chip
            key={index}
            variant={selectedValue === option.value ? 'filled' : 'outlined'}
            color={selectedValue === option.value ? 'primary' : 'default'}
            size='small'
            label={option.label}
            onClick={() => handleChange(option.value)}
            disabled={isDisabled}
            sx={{
              height: 50,
              borderRadius: 6,
              fontSize: 14,
              fontWeight: 500,
              px: 4,
              ':hover': { fontSize: 14, fontWeight: 500 },
              '&.MuiChip-outlined': { px: 3.875 },
            }}
          />
        ))}
      </Flex>
      {helperText ? <FormHelperText sx={{ mt: 2 }}>{helperText}</FormHelperText> : null}
    </FormControl>
  );
});

ChipSelection.displayName = 'ChipSelection';

export { ChipSelection };
export type { ChipSelectionProps };
